"use client";

/**
 * Itinerary Section Component
 * Main container for displaying tour itinerary with all sections
 * - Optional pre-night/pre-tour
 * - Main tour days (3 visible initially, expandable)
 * - Optional post-tour/post-night
 * - Timeline in left column with continuous line and dots
 */

import React, { useState, useRef, useMemo, useCallback } from "react";
import { Box, Stack, Typography } from "@mui/material";
import type { ExtensionStyle, Itinerary } from "@/types/dto/pdp/pdp-itinerary";
import { ActivityLevel } from "@/types/dto/pdp/pdp-header-about";
import { useTranslations } from "next-intl";
import {
  PrePostNightAccordion,
  ExtensionAccordion,
  DayCard,
  TimelineColumn,
  ShowFullItineraryButton,
  type TimelineItem
} from "./components";
import ItinerarySectionSkeleton from "./itinerary-section-skeleton";

interface ItinerarySectionProps {
  itinerary?: Itinerary;
  testIdPrefix?: string;
  handleOpenExtensionStyles: (extensionStyle: string) => void;
  extensionStyleProps: ExtensionStyle[];
  isLoading: boolean;
  handleOpenActivityLevel: (defaultLevel: ActivityLevel["number"]) => void;
}

export const ITINERARY_SECTION_ID = "itinerary-section";

const ItinerarySection: React.FC<ItinerarySectionProps> = ({
  itinerary,
  testIdPrefix = "pdp",
  handleOpenExtensionStyles,
  extensionStyleProps,
  isLoading,
  handleOpenActivityLevel
}) => {
  const [showAllDays, setShowAllDays] = useState(false);
  const initialDaysToShow = 3;
  const sectionRef = useRef<HTMLDivElement>(null);

  const mainTourDays = useMemo(() => itinerary?.days || [], [itinerary?.days]);
  const visibleDays = useMemo(
    () => (showAllDays ? mainTourDays : mainTourDays.slice(0, initialDaysToShow)),
    [showAllDays, mainTourDays, initialDaysToShow]
  );

  const t = useTranslations("PDP");

  const handleToggleItinerary = () => {
    if (showAllDays) {
      // When collapsing, scroll back to the section top with smooth animation
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setShowAllDays(!showAllDays);
  };

  // DOM node storage for timeline measurement (React 19/2025 best practice)
  // Store actual DOM nodes, not RefObjects, for use with callback refs
  // Array grows dynamically with content, no pre-allocation needed
  const contentNodes = useRef<(HTMLDivElement | null)[]>([]);

  // Callback ref factory - stable function that returns a callback for each index
  // This pattern gives us fine-grain control over when refs are set/unset
  const setContentNode = useCallback(
    (index: number) => (node: HTMLDivElement | null) => {
      contentNodes.current[index] = node;
    },
    []
  );

  // Calculate stable indices for each content section
  // These indices determine where each section stores its DOM node in contentNodes array
  const indices = useMemo(() => {
    let currentIndex = 0;
    const preNightIdx = itinerary?.preNight?.city ? currentIndex : -1;
    if (itinerary?.preNight?.city) currentIndex += 1;

    const preTourIdx = itinerary?.preTour?.title ? currentIndex : -1;
    if (itinerary?.preTour?.title) currentIndex += 1;

    const firstDayIdx = currentIndex;
    currentIndex += visibleDays.length;
    const lastDayIdx = currentIndex - 1;

    const postTourIdx = itinerary?.postTour?.title ? currentIndex : -1;
    if (itinerary?.postTour?.title) currentIndex += 1;

    const postNightIdx = itinerary?.postNight?.city ? currentIndex : -1;

    return {
      preNight: preNightIdx,
      preTour: preTourIdx,
      firstDay: firstDayIdx,
      lastDay: lastDayIdx,
      postTour: postTourIdx,
      postNight: postNightIdx
    };
  }, [
    itinerary?.preNight?.city,
    itinerary?.preTour?.title,
    itinerary?.postTour?.title,
    itinerary?.postNight?.city,
    visibleDays.length
  ]);

  // Build timeline items array (metadata only, no refs)
  const timelineItems = useMemo<TimelineItem[]>(() => {
    const items: TimelineItem[] = [];

    // Add optional pre-night
    if (itinerary?.preNight?.city) {
      items.push({
        type: "optional",
        hasBullet: true // Every section gets a dot
      });
    }

    // Add optional pre-tour
    if (itinerary?.preTour?.title) {
      items.push({
        type: "optional",
        hasBullet: true // Every section gets a dot
      });
    }

    // Header is NOT added to timeline - it's just a visual divider

    // Add main tour days
    for (let i = 0; i < visibleDays.length; i += 1) {
      items.push({
        type: "main",
        hasBullet: true // Every section gets a dot
      });
    }

    // This makes the timeline end at the last visible day when collapsed
    // Add optional post-tour
    if (itinerary?.postTour?.title) {
      items.push({
        type: "optional",
        hasBullet: true // Every section gets a dot
      });
    }

    // Add optional post-night
    if (itinerary?.postNight?.city) {
      items.push({
        type: "optional",
        hasBullet: true // Every section gets a dot
      });
    }

    return items;
  }, [itinerary, visibleDays.length, showAllDays]);

  if (isLoading || !itinerary) {
    return <ItinerarySectionSkeleton />;
  }

  // Don't render section if no meaningful itinerary content is available
  if (
    (!itinerary.days || itinerary.days.length === 0) &&
    !itinerary.preNight &&
    !itinerary.preTour &&
    !itinerary.postTour &&
    !itinerary.postNight
  ) {
    return null;
  }

  return (
    <Box
      ref={sectionRef}
      id={ITINERARY_SECTION_ID}
      data-testid={`${testIdPrefix}_itinerary-section`}
      data-track="visibility"
      data-module-name="Itinerary Section"
      data-parent-container-type="pdp"
      data-ranking-algorithm="manual"
      data-module-ranking-position="8"
      component="section"
      sx={{
        width: "100%",
        padding: { xs: "unset", legacyTablet: "unset", legacyDesktop: "2rem" },
        marginBottom: { xs: "2rem", legacyTablet: "2rem", legacyDesktop: 0 }
      }}
    >
      {/* Two-column layout: Timeline left, Content right */}
      <Box sx={{ display: "flex", gap: "2.4rem" }}>
        {/* Timeline Column (hidden on mobile) */}
        <TimelineColumn
          items={timelineItems}
          nodes={contentNodes.current}
          testIdPrefix={testIdPrefix}
        />

        {/* Content Column */}
        <Box sx={{ flex: 1 }}>
          <Stack gap="0">
            {/* Pre-Night (Optional) */}
            {itinerary?.preNight?.city && (
              <Box ref={setContentNode(indices.preNight)} sx={{ marginTop: "1rem" }}>
                <PrePostNightAccordion
                  night={itinerary?.preNight}
                  type="pre"
                  testIdPrefix={`${testIdPrefix}-pre-night`}
                />
              </Box>
            )}

            {/* Pre-Tour (Optional) */}
            {itinerary?.preTour?.title && (
              <Box ref={setContentNode(indices.preTour)} sx={{ marginTop: "1rem" }}>
                <ExtensionAccordion
                  extension={itinerary?.preTour}
                  type="pre"
                  testIdPrefix={`${testIdPrefix}-pre-tour`}
                  handleOpenExtensionStyles={handleOpenExtensionStyles}
                  handleOpenActivityLevel={handleOpenActivityLevel}
                  extensionStyleProps={extensionStyleProps}
                />
              </Box>
            )}

            {/* Itinerary Header - No ref, not part of timeline */}
            <Box
              sx={{
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                padding: "2.4rem",
                marginTop: 0
              }}
            >
              <Typography
                component="h3"
                sx={{
                  fontSize: { xs: "1.8rem", md: "2rem" },
                  fontWeight: 600,
                  lineHeight: 1.3,
                  color: "text.primary",
                  margin: 0
                }}
              >
                {t("Itinerary Day by Day")}
              </Typography>
            </Box>

            {/* Main Tour Days */}
            {visibleDays.map((day, index) => (
              <Box key={day.dayNumber} ref={setContentNode(indices.firstDay + index)}>
                <DayCard day={day} dayNumber={day.dayNumber} testIdPrefix={testIdPrefix} />
              </Box>
            ))}

            {/* Show Full Itinerary / Collapse Button */}
            {/* WHY: Button at bottom (after all content) toggles between "Show Full Itinerary" and "Show Less" */}
            {mainTourDays.length > initialDaysToShow && (
              <Box sx={{ padding: "2.4rem 0" }}>
                <ShowFullItineraryButton
                  isExpanded={showAllDays}
                  onClick={handleToggleItinerary}
                  testIdPrefix={testIdPrefix}
                />
              </Box>
            )}
            {/* Post-Tour (Optional) */}
            {itinerary?.postTour?.title && (
              <Box ref={setContentNode(indices.postTour)} sx={{ marginTop: "1rem" }}>
                <ExtensionAccordion
                  extension={itinerary?.postTour}
                  type="post"
                  testIdPrefix={`${testIdPrefix}-post-tour`}
                  handleOpenExtensionStyles={handleOpenExtensionStyles}
                  handleOpenActivityLevel={handleOpenActivityLevel}
                  extensionStyleProps={extensionStyleProps}
                />
              </Box>
            )}
            {/* Post-Night (Optional) */}
            {itinerary?.postNight?.city && (
              <Box ref={setContentNode(indices.postNight)} sx={{ marginTop: "1rem" }}>
                <PrePostNightAccordion
                  night={itinerary?.postNight}
                  type="post"
                  testIdPrefix={`${testIdPrefix}-post-night`}
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default ItinerarySection;
